import type { ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import Box from '@mui/material/Box';
import { NotFoundPage } from '@/pages/error/NotFoundPage';
import { UnauthorizedPage } from '@/pages/error/UnauthorizedPage';
import { ServerErrorPage } from '@/pages/error/ServerErrorPage';

/** Set right before a reload triggered by a stale chunk, cleared once the app mounts cleanly —
 * guards against reload-looping if the deployed build is genuinely broken rather than just stale. */
export const CHUNK_RELOAD_STORAGE_KEY = 'app:chunk-reload-attempted';

/** Vite/the browser throw this shape when a lazy-loaded route's JS chunk 404s — typically because
 * the app was redeployed after the chunk was fetched and the old filename hash no longer exists.
 * A single reload picks up the new build's asset manifest and resolves it. */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(message);
}

function CenteredError({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 2 }}>
      {children}
    </Box>
  );
}

/** Root-level errorElement — catches anything an errorElement-less descendant route doesn't
 * handle itself: thrown loader/action errors, unexpected render-phase exceptions, and stale
 * dynamic-import chunk failures. Without this, React Router falls back to its own bare-bones
 * default error screen. */
export function RouteErrorBoundary() {
  const error = useRouteError();

  if (isStaleChunkError(error)) {
    if (!sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, '1');
      window.location.reload();
      return null;
    }
    // Already retried once this session and it's still failing — stop looping and let the
    // user retry manually instead of reloading forever.
    return (
      <CenteredError>
        <ServerErrorPage onRetry={() => window.location.reload()} />
      </CenteredError>
    );
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <CenteredError>
        <NotFoundPage />
      </CenteredError>
    );
  }

  if (isRouteErrorResponse(error) && (error.status === 401 || error.status === 403)) {
    return (
      <CenteredError>
        <UnauthorizedPage />
      </CenteredError>
    );
  }

  return (
    <CenteredError>
      <ServerErrorPage onRetry={() => window.location.reload()} />
    </CenteredError>
  );
}
